import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { resolveAccessError } from "@/shared/organizations/access-errors";
import { useRenameOrganization } from "@/shared/organizations/use-access-mutations";
import { ORGANIZATION_NAME_MAX_LENGTH, ORGANIZATION_NAME_MIN_LENGTH } from "./constants";

interface RenameOrganizationDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organizationId: string;
  readonly currentName: string;
  /** The current name is the auto-generated one: saving it unchanged confirms it. */
  readonly nameIsDerived?: boolean;
  /** Fired with the saved name, right before the dialog closes. */
  readonly onDone: (name: string) => void;
}

/**
 * Edit the organization name (backoffice ADMIN). The name is trimmed and must
 * be 2 to 30 characters; the API enforces the same rules and has no
 * uniqueness constraint.
 */
export function RenameOrganizationDialog(props: RenameOrganizationDialogProps) {
  // Mounted only while open: the field always starts from the current name.
  if (!props.open) return null;
  return <RenameOrganizationDialogBody {...props} />;
}

function RenameOrganizationDialogBody({
  onOpenChange,
  organizationId,
  currentName,
  nameIsDerived = false,
  onDone,
}: RenameOrganizationDialogProps) {
  const { t } = useTranslation();
  const rename = useRenameOrganization();
  const [value, setValue] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const trimmed = value.trim();
  const valid =
    trimmed.length >= ORGANIZATION_NAME_MIN_LENGTH &&
    trimmed.length <= ORGANIZATION_NAME_MAX_LENGTH;
  const changed = trimmed !== currentName.trim();
  const canSubmit = valid && (changed || nameIsDerived) && !rename.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      await rename.mutateAsync({ organizationId, name: trimmed });
      onDone(trimmed);
      onOpenChange(false);
    } catch (err) {
      setError(resolveAccessError(err, t));
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && rename.isPending) return;
    onOpenChange(next);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="rename-org-dialog"
        className="max-w-[calc(100vw-2rem)] sm:max-w-md"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{t("organizationAccess.rename.title")}</DialogTitle>
            <DialogDescription>{t("organizationAccess.rename.description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="rename-org-input">{t("organizationAccess.rename.label")}</Label>
            <Input
              id="rename-org-input"
              data-testid="rename-org-input"
              value={value}
              maxLength={ORGANIZATION_NAME_MAX_LENGTH * 2}
              disabled={rename.isPending}
              onChange={(event) => setValue(event.target.value)}
              aria-invalid={!valid}
            />
            {nameIsDerived && (
              <p data-testid="rename-org-derived" className="text-xs text-muted-foreground">
                {t("organizationAccess.rename.derivedHint")}
              </p>
            )}
            {valid ? (
              <p className="text-xs text-muted-foreground">{t("organizationAccess.rename.hint")}</p>
            ) : (
              <p data-testid="rename-org-invalid" className="text-xs text-destructive">
                {t("organizationAccess.rename.invalid")}
              </p>
            )}
          </div>

          {error && (
            <div
              data-testid="rename-org-error"
              role="alert"
              className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
            >
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-destructive">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              data-testid="rename-org-cancel"
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={rename.isPending}
            >
              {t("organizationAccess.cancel")}
            </Button>
            <Button data-testid="rename-org-submit" type="submit" disabled={!canSubmit}>
              {rename.isPending
                ? t("organizationAccess.rename.pending")
                : t("organizationAccess.rename.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
